"use client";

import React, { useState } from "react";
import Link from "next/link";
import {
  MessageSquare,
  X,
  Phone,
  Mail,
  Calendar,
  Layers,
  ArrowUpRight,
  ShieldCheck,
  Headphones,
  Check,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePathname } from "next/navigation";
import { CopyButton } from "../ui/CopyButton";

export function FloatingSupport() {
  const pathname = usePathname();
  if (pathname?.startsWith("/crm")) return null;

  const [isOpen, setIsOpen] = useState(false);

  const phone = "919880893416";
  const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(
    "Hi Johnson Softwares, I'm reviewing your software engineering services and would like to discuss an enterprise project."
  )}`;

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end print:hidden">
      {/* Expanded Support Hub Card */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 15 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="mb-3 w-84 max-w-[calc(100vw-2.5rem)] rounded-2xl p-4 shadow-2xl border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl overscroll-contain"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="relative">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-blue-600 to-cyan-500 flex items-center justify-center text-white font-black text-xs shadow-md">
                    JS
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900 animate-pulse" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-xs text-slate-900 dark:text-slate-100">
                    Johnson Softwares Direct Desk
                  </h4>
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                    <span>●</span> Chief Architect Online
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                aria-label="Close support card"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="py-2.5 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Connect directly with our senior engineering leads for technology audits, scoping estimates, or custom software demos.
            </p>

            {/* Channels Grid */}
            <div className="space-y-2">
              {/* WhatsApp Live */}
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2 px-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-500/25 font-heading font-bold text-xs flex items-center justify-between transition-all group"
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-emerald-500" />
                  <span>Chat on WhatsApp</span>
                </div>
                <div className="flex items-center gap-1 text-[10px] font-normal text-emerald-600 dark:text-emerald-400">
                  <span>Avg &lt; 5m</span>
                  <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </a>

              {/* Direct Phone */}
              <a
                href="tel:+919880893416"
                className="w-full py-2 px-3 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-500/25 font-heading font-bold text-xs flex items-center justify-between transition-all group"
              >
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-500" />
                  <span>Call Founder (+91 98808 93416)</span>
                </div>
                <ArrowUpRight className="w-3 h-3 text-blue-400 group-hover:translate-x-0.5 transition-transform" />
              </a>

              {/* Email Copy */}
              <div className="w-full py-2 px-3 rounded-xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-xs flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="truncate max-w-[150px]">contact@johnsonsoftwares.com</span>
                </div>
                <CopyButton text="contact@johnsonsoftwares.com" variant="subtle" label="Copy" showToast />
              </div>

              {/* Consultation Link */}
              <Link
                href="/contact"
                onClick={() => setIsOpen(false)}
                className="w-full py-2 px-3 rounded-xl bg-slate-900 dark:bg-slate-100 hover:bg-slate-800 dark:hover:bg-white text-white dark:text-slate-900 font-heading font-bold text-xs flex items-center justify-between transition-all shadow-md mt-1"
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Schedule Scoping Audit</span>
                </div>
                <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Guarantee Footer */}
            <div className="pt-2.5 mt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-400">
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <ShieldCheck className="w-3 h-3" /> Zero SaaS Tax Promise
              </span>
              <Link
                href="/demos"
                onClick={() => setIsOpen(false)}
                className="hover:text-blue-500 dark:hover:text-cyan-400 transition-colors flex items-center gap-0.5"
              >
                <Layers className="w-3 h-3" />
                <span>All 8 Demos</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Trigger Buttons */}
      <div className="flex items-center gap-2">
        {/* 1-Tap Direct WhatsApp Button */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          className="flex items-center justify-center w-11 h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white shadow-xl shadow-emerald-600/30 hover:scale-105 active:scale-95 transition-all cursor-pointer"
        >
          <MessageSquare className="w-5 h-5 text-white" />
        </a>

        {/* Quick Connect Hub Pill */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Open support and quick connect hub"
          className="group flex items-center gap-2 px-3.5 py-2.5 rounded-full bg-slate-900 dark:bg-slate-800 text-white border border-slate-700/80 shadow-xl hover:shadow-cyan-500/10 hover:border-cyan-500/50 active:scale-95 transition-all cursor-pointer"
        >
          <div className="relative flex items-center justify-center">
            <Headphones className="w-4 h-4 text-cyan-400 group-hover:scale-110 transition-transform" />
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
          </div>
          <span className="text-xs font-heading font-bold tracking-wide">
            {isOpen ? "Close" : "Talk to Architect"}
          </span>
        </button>
      </div>
    </div>
  );
}
